// Validaciones compartidas entre la app y las Edge Functions.
// Cada función devuelve null si el valor es válido, o un mensaje para mostrar.

export function normalizeUsername(value: string): string {
  return value.trim().toLowerCase();
}

export function validateUsername(value: string): string | null {
  const v = normalizeUsername(value);
  if (v.length < 3) return 'El usuario debe tener al menos 3 caracteres.';
  if (v.length > 30) return 'El usuario puede tener hasta 30 caracteres.';
  if (!/^[a-z0-9._-]+$/.test(v)) return 'Usá solo letras sin acentos, números, punto, guion o guion bajo.';
  return null;
}

export function validatePin(value: string): string | null {
  if (!/^\d{4,6}$/.test(value)) return 'El PIN debe tener entre 4 y 6 números.';
  if (/^(\d)\1+$/.test(value)) return 'El PIN no puede ser el mismo número repetido.';
  const digits = [...value].map(Number);
  const ascending = digits.every((d, i) => i === 0 || d === (digits[i - 1]! + 1) % 10);
  const descending = digits.every((d, i) => i === 0 || d === (digits[i - 1]! + 9) % 10);
  if (ascending || descending) return 'El PIN no puede ser una secuencia (como 1234).';
  return null;
}

export function validatePassword(value: string): string | null {
  if (value.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
  return null;
}

export function validateDisplayName(value: string): string | null {
  const v = value.trim();
  if (v.length < 2) return 'El nombre debe tener al menos 2 caracteres.';
  if (v.length > 60) return 'El nombre puede tener hasta 60 caracteres.';
  return null;
}

export function validateBusinessName(value: string): string | null {
  const v = value.trim();
  if (v.length < 2) return 'El nombre del negocio debe tener al menos 2 caracteres.';
  if (v.length > 80) return 'El nombre del negocio puede tener hasta 80 caracteres.';
  return null;
}

export function validateEmail(value: string): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Ingresá un email válido.';
  return null;
}

export function validateHexColor(value: string): string | null {
  if (!/^#[0-9a-f]{6}$/i.test(value)) return 'Color inválido.';
  return null;
}

/** Teléfono laxo: se guardan solo dígitos (y + inicial). */
/**
 * Número para los links de WhatsApp (wa.me): internacional, solo dígitos. Los celulares argentinos
 * van como 549 + código de área + número (10 dígitos), sin el 0 ni el 15. Acepta las formas
 * habituales de escribirlos: "11 5555-5555", "011 15 5555-5555", "+54 9 11 5555 5555",
 * "54 11 5555 5555", "2214 15 55 5555"… Un número con + de otro país queda como está.
 * Devuelve null si no se puede interpretar.
 */
export function whatsappNumber(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  let digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (raw.startsWith('+') && !digits.startsWith('54')) return digits.length >= 8 ? digits : null;
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('54')) {
    digits = digits.slice(2);
    if (digits.startsWith('9')) digits = digits.slice(1);
  }
  if (digits.startsWith('0')) digits = digits.slice(1);
  // Área (2 a 4 dígitos) + 15 + número: se saca el 15.
  if (digits.length === 12) {
    for (const area of [2, 3, 4]) {
      if (digits.slice(area, area + 2) === '15') {
        digits = digits.slice(0, area) + digits.slice(area + 2);
        break;
      }
    }
  }
  return digits.length === 10 ? `549${digits}` : null;
}

export function normalizePhone(value: string): string {
  const trimmed = value.trim();
  const plus = trimmed.startsWith('+') ? '+' : '';
  return plus + trimmed.replace(/\D/g, '');
}
